import { Type } from '@angular/core';
import { Store } from '@ngrx/store';
import { actions, selectors } from '@twaice-fe/frontend/shared/store';
import { ChatMessage } from '@twaice-fe/shared/models';
import { map, Observable, of } from 'rxjs';
import {
  BreachedRuleSummary,
  RuleActivity,
  RuleChatMessagePost,
  RuleDetails,
  RuleEvaluation,
  TimeRangeIsoStr,
} from '../../component-sidebar.types';

const { chatActions } = actions;
const { chatSelectors, systemSelectors } = selectors;

export interface RuleEvaluationsChartState {
  isLoading$: Observable<boolean>;
  emptyState$: Observable<boolean>;
  errorState$: Observable<boolean>;
}

export abstract class ComponentRuleBreachDetailsService {
  readonly additionalActivityLabel: string | null = null;

  protected constructor(protected store: Store) {}

  /**
   * The window requested from the API. Pages that provide it get a breach-history x-axis anchored
   * to it; the default leaves the chart deriving its extent from the returned data.
   */
  getRequestedTimeRange$(): Observable<TimeRangeIsoStr | null> {
    return of(null);
  }

  // Shown in the selected system's timezone, like the other times in the panel.
  getStorageTimezone$(): Observable<string | null> {
    return this.store.select(systemSelectors.getSelected).pipe(map((system) => system?.bessMetadata?.storageTimezone ?? null));
  }

  getChatMessages$(): Observable<ChatMessage[] | null> {
    return this.store.select(chatSelectors.getChatMessages);
  }

  fetchChatMessages(): void {
    this.store.dispatch(chatActions.fetchChatMessages());
  }

  postChatMessage(post: RuleChatMessagePost): void {
    this.store.dispatch(chatActions.postChatMessage(post));
  }

  getAdditionalActivity$(): Observable<RuleActivity[]> {
    return of([]);
  }

  getMinBreachDurationLabel$(_ruleBk: string): Observable<string | null> {
    return of(null);
  }

  // Pages whose chart depends on store state override this; the rest keep answering synchronously.
  getSensorTimeseriesComponent$(rule: BreachedRuleSummary): Observable<Type<unknown> | null> {
    return of(this.getSensorTimeseriesComponent(rule));
  }

  abstract getRuleEvaluationsChartState(ruleBk: string): RuleEvaluationsChartState;
  abstract getRuleEvaluations$(ruleBk: string): Observable<RuleEvaluation[] | null>;
  abstract getRuleEvaluationsLoadingState$(ruleBk: string): Observable<boolean>;
  abstract getSensorTimeseriesComponent(rule: BreachedRuleSummary): Type<unknown> | null;
  abstract getRuleDetails$(ruleBk: string): Observable<RuleDetails | undefined>;
  abstract getSidebarLookbackDays$(): Observable<number>;
}
